import React, { useCallback, useLayoutEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { type SafeAreaViewProps } from 'react-native-safe-area-context';
import {
  type RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { DisplayText, theme } from '@sg/garnish';

import { StripeCustomerSheet } from '@order/integrations/stripe';

import { PageWrapper, SignedOutView } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import type {
  ModalStackParamList,
  PaymentsScreenProps,
} from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const PaymentsScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const navigation = useNavigation<PaymentsScreenProps['navigation']>();
  const { params } = useRoute<RouteProp<ModalStackParamList, 'Payments'>>();
  const redirectTo = params?.redirectTo;

  // ─────────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation.navigate('ScanAtCheckout');
  }, [navigation]);

  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: true, presentation: 'modal' });
  }, [navigation]);

  if (!isLoggedIn) {
    return <SignedOutView redirectTo="payments" withoutFooter />;
  }

  return (
    <PageWrapper
      safeAreaEdges={safeAreaAndroid}
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
      withHeaderTitle
      withoutFooter
    >
      <DisplayText sizeMatch={['24']}>
        <FormattedMessage {...messages.title} />
      </DisplayText>

      <StripeCustomerSheet
        visible
        redirectTo={redirectTo}
        onClose={handleClose}
      />
    </PageWrapper>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Payments',
    description: 'Payments Screen | Title',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const safeAreaAndroid: SafeAreaViewProps['edges'] = Platform.select({
  android: ['top'],
  default: [],
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    padding: theme.spacing['4'],
  },
  content: {
    flex: 1,
    gap: theme.spacing['4'],
  },
});
